import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Filter,
  HardHat,
  Truck,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { useSchedule, type ScheduleEvent, type ScheduleFilters, type ScheduleResources } from '../hooks/useSchedule';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MoveCard } from '../components/ui/MoveCard';
import { formatMoveDate, JOB_STATUS_LABEL, JOB_STATUS_CLASSES, QUOTE_STATUS_LABEL, QUOTE_STATUS_CLASSES } from '../types/admin';
import type { JobStatus, QuoteStatus } from '../types/admin';

// ── Helpers ──────────────────────────────────────────────────────────────────

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Get Monday of the week containing dateStr. */
function getMonday(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(dateStr, diff);
}

const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatWeekRange(mondayStr: string): string {
  const mon = new Date(mondayStr + 'T00:00:00');
  const sun = new Date(mondayStr + 'T00:00:00');
  sun.setDate(sun.getDate() + 6);
  const monMonth = mon.toLocaleDateString('en-US', { month: 'short' });
  const sunMonth = sun.toLocaleDateString('en-US', { month: 'short' });
  if (monMonth === sunMonth) {
    return `${monMonth} ${mon.getDate()}–${sun.getDate()}`;
  }
  return `${monMonth} ${mon.getDate()} – ${sunMonth} ${sun.getDate()}`;
}

function formatDayHeader(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function getDateDay(dateStr: string): number {
  return new Date(dateStr + 'T00:00:00').getDate();
}

// ── Schedule Event Count ─────────────────────────────────────────────────────

export interface ScheduleCounts {
  totalEvents: number;
}

// ── Filter Sheet ─────────────────────────────────────────────────────────────

function ScheduleFilterSheet({
  filters,
  onApply,
  onClose,
}: {
  filters: ScheduleFilters;
  onApply: (f: ScheduleFilters) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<ScheduleFilters>({ ...filters });

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-sm md:w-full md:pb-6 max-h-[70vh] flex flex-col">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-h3 text-midnight">Filters</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-mist">
            <X size={18} className="text-slate" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-5">
          {/* Stage filter */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-2">Event Type</p>
            <div className="flex gap-2">
              {([undefined, 'job', 'quote'] as const).map(val => (
                <button
                  key={val ?? 'all'}
                  type="button"
                  onClick={() => setLocal(prev => ({ ...prev, stage: val as ScheduleFilters['stage'] }))}
                  className={`flex-1 h-9 rounded-lg text-[13px] font-medium border transition-colors
                    ${local.stage === val
                      ? 'border-plum bg-plum/5 text-plum'
                      : 'border-stone bg-white text-slate hover:border-plum/30'}`}
                >
                  {val === undefined ? 'All' : val === 'job' ? 'Moves' : 'Estimates'}
                </button>
              ))}
            </div>
          </div>

          {/* Type filter */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-2">Quote Type</p>
            <div className="flex gap-2">
              {([undefined, 'commercial', 'residential'] as const).map(val => (
                <button
                  key={val ?? 'all'}
                  type="button"
                  onClick={() => setLocal(prev => ({ ...prev, type: val as ScheduleFilters['type'] }))}
                  className={`flex-1 h-9 rounded-lg text-[13px] font-medium border transition-colors
                    ${local.type === val
                      ? 'border-plum bg-plum/5 text-plum'
                      : 'border-stone bg-white text-slate hover:border-plum/30'}`}
                >
                  {val === undefined ? 'All' : val === 'commercial' ? 'Commercial' : 'Residential'}
                </button>
              ))}
            </div>
          </div>

          {/* Job status filter */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate mb-2">Job Status</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setLocal(prev => ({ ...prev, status: undefined }))}
                className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors
                  ${!local.status
                    ? 'border-plum bg-plum/5 text-plum'
                    : 'border-stone bg-white text-slate hover:border-plum/30'}`}
              >
                All
              </button>
              {(['scheduled', 'in_progress', 'completed', 'on_hold'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setLocal(prev => ({ ...prev, status: prev.status === s ? undefined : s }))}
                  className={`h-8 px-3 rounded-lg text-[12px] font-medium border transition-colors
                    ${local.status === s
                      ? 'border-plum bg-plum/5 text-plum'
                      : 'border-stone bg-white text-slate hover:border-plum/30'}`}
                >
                  {JOB_STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5 pt-4 border-t border-stone">
          <button
            type="button"
            onClick={() => { setLocal({}); onApply({}); onClose(); }}
            className="flex-1 h-10 rounded-lg border border-stone text-[13px] font-medium text-slate hover:bg-mist transition-colors"
          >
            Clear All
          </button>
          <button
            type="button"
            onClick={() => { onApply(local); onClose(); }}
            className="flex-1 h-10 rounded-lg bg-plum text-white text-[13px] font-medium hover:bg-rose transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Schedule Event → MoveCard adapter ──────────────────────────────────────

function scheduleEventToMoveCard(event: ScheduleEvent) {
  const isMove = event.type === 'move';
  const stage = isMove ? 'job' as const : 'quote' as const;

  const statusLabel = isMove
    ? (event.jobStage && event.jobStatus === 'in_progress'
        ? event.jobStage.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : JOB_STATUS_LABEL[event.jobStatus as JobStatus] ?? event.jobStatus ?? 'Scheduled')
    : (QUOTE_STATUS_LABEL[event.quoteStatus as QuoteStatus] ?? event.quoteStatus?.replace(/_/g, ' ') ?? 'Draft');

  const statusClasses = isMove
    ? (JOB_STATUS_CLASSES[event.jobStatus as JobStatus] ?? 'bg-stone text-slate')
    : (QUOTE_STATUS_CLASSES[event.quoteStatus as QuoteStatus] ?? 'bg-stone text-slate');

  // Adapt flat ScheduleEvent shape to what MoveCard helpers expect
  const mappedData = {
    id: event.id,
    customerCompanyName: event.customerCompanyName,
    quoteNumber: event.quoteNumber,
    type: event.quoteType ?? 'commercial',
    movingDate: isMove && event.date ? { preferredDate: event.date } : undefined,
    estimateVisitDate: !isMove ? event.date : undefined,
    addresses: {
      origin: event.originAddress ? { address: event.originAddress } : undefined,
      destination: event.destinationAddress ? { address: event.destinationAddress } : undefined,
    },
    logisticsSummary: { crewSize: event.crewSize, truckQuantity: event.truckQuantity },
    assignedCrew: event.assignedCrew,
    assignedTrucks: event.assignedTrucks,
    quoteLeadDisplayName: event.quoteLeadDisplayName,
  };

  return { stage, statusLabel, statusClasses, mappedData };
}

function ScheduleMoveCard({ event, onClick }: { event: ScheduleEvent; onClick: () => void }) {
  const { stage, statusLabel, statusClasses, mappedData } = scheduleEventToMoveCard(event);
  return <MoveCard data={mappedData} stage={stage} statusLabel={statusLabel} statusClasses={statusClasses} onClick={onClick} />;
}

// ── Main Component ───────────────────────────────────────────────────────────

export function ScheduleContent({
  orgId,
  onCountsChange,
  onSidebarContent,
}: {
  orgId: string;
  onCountsChange?: (counts: ScheduleCounts) => void;
  onSidebarContent?: (content: React.ReactNode) => void;
}) {
  const navigate = useNavigate();
  const today = todayStr();

  // State
  const [selectedDate, setSelectedDate] = useState(today);
  const [weekMonday, setWeekMonday] = useState(() => getMonday(today));
  const [filters, setFilters] = useState<ScheduleFilters>({});
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // Fetch schedule data for the full week
  const { events, dayCounts, resources, loading, error } = useSchedule(orgId, selectedDate, 'week', filters);

  // Notify parent of event count
  const totalEvents = events.length;
  useMemo(() => {
    onCountsChange?.({ totalEvents });
  }, [totalEvents, onCountsChange]);

  // Events for the selected day only
  const dayEvents = useMemo(() => {
    return events.filter(e => e.date === selectedDate);
  }, [events, selectedDate]);

  // Group events by type for the day
  const moveEvents = useMemo(() => dayEvents.filter(e => e.type === 'move'), [dayEvents]);
  const estimateEvents = useMemo(() => dayEvents.filter(e => e.type === 'estimate'), [dayEvents]);

  // Resource view mode
  const [resourceMode, setResourceMode] = useState<'today' | 'weekly'>('today');

  // Week navigation
  const goToPrevWeek = useCallback(() => {
    const newMon = addDays(weekMonday, -7);
    setWeekMonday(newMon);
    setSelectedDate(newMon);
  }, [weekMonday]);

  const goToNextWeek = useCallback(() => {
    const newMon = addDays(weekMonday, 7);
    setWeekMonday(newMon);
    setSelectedDate(newMon);
  }, [weekMonday]);

  const goToToday = useCallback(() => {
    const t = todayStr();
    setWeekMonday(getMonday(t));
    setSelectedDate(t);
  }, []);

  const selectDay = useCallback((dateStr: string) => {
    setSelectedDate(dateStr);
  }, []);

  // Generate week day data
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(weekMonday, i);
      return {
        date: d,
        dayNum: getDateDay(d),
        dayName: DAY_NAMES_SHORT[i],
        count: dayCounts[d] ?? 0,
        isToday: d === today,
        isSelected: d === selectedDate,
      };
    });
  }, [weekMonday, dayCounts, today, selectedDate]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.stage) n++;
    if (filters.type) n++;
    if (filters.status) n++;
    return n;
  }, [filters]);

  function handleEventClick(event: ScheduleEvent) {
    navigate(`/moves/${event.id}`);
  }

  // Provide sidebar content for desktop two-column layout
  useEffect(() => {
    onSidebarContent?.(
      <ResourceSummary
        resources={resources}
        dayEvents={dayEvents}
        allEvents={events}
        selectedDate={selectedDate}
        today={today}
        mode={resourceMode}
        onModeChange={setResourceMode}
      />,
    );
  }, [onSidebarContent, resources, dayEvents, events, selectedDate, today, resourceMode]);

  return (
    <>
      {/* Week navigation header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={goToPrevWeek}
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone bg-white text-slate hover:border-plum/30 hover:text-midnight transition-colors"
          aria-label="Previous week"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <p className="text-[13px] font-semibold text-midnight">{formatWeekRange(weekMonday)}</p>
          {selectedDate !== today && (
            <button
              type="button"
              onClick={goToToday}
              className="text-[11px] text-plum font-medium hover:underline mt-0.5"
            >
              Go to today
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={goToNextWeek}
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-stone bg-white text-slate hover:border-plum/30 hover:text-midnight transition-colors"
          aria-label="Next week"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Week strip */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {weekDays.map(day => (
          <button
            key={day.date}
            type="button"
            onClick={() => selectDay(day.date)}
            className={`flex flex-col items-center py-2 rounded-xl transition-all duration-150 min-h-[64px]
              ${day.isSelected
                ? 'bg-plum text-white shadow-brand-sm'
                : day.isToday
                  ? 'bg-plum/10 text-plum border border-plum/20'
                  : 'bg-mist text-slate hover:bg-stone/60'}`}
          >
            <span className={`text-[10px] font-bold uppercase tracking-wider ${day.isSelected ? 'text-white/70' : ''}`}>
              {day.dayName}
            </span>
            <span className={`text-[16px] font-semibold leading-tight mt-0.5 ${day.isSelected ? 'text-white' : 'text-midnight'}`}>
              {day.dayNum}
            </span>
            {/* Event count dots */}
            {day.count > 0 && (
              <span className={`text-[10px] font-bold mt-0.5 ${day.isSelected ? 'text-white/80' : 'text-plum'}`}>
                {day.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Day header + filter button */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-midnight">
            {selectedDate === today ? 'Today' : formatDayHeader(selectedDate)}
          </h2>
          <p className="text-[12px] text-slate">
            {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
            {selectedDate === today ? '' : ` · ${formatMoveDate(selectedDate)}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          className={`shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border transition-colors duration-150 relative
            ${activeFilterCount > 0
              ? 'border-plum bg-plum/5 text-plum'
              : 'border-stone bg-white text-slate hover:border-plum/30 hover:text-midnight'}`}
          aria-label="Filter"
        >
          <Filter size={16} />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-plum text-white text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">{error}</p>
        </div>
      )}

      {/* Day agenda — mobile layout */}
      {!loading && !error && (
        <div className="lg:hidden space-y-4">
          {/* Empty state for events */}
          {dayEvents.length === 0 && (
            <div className="flex flex-col items-center py-16 gap-3 text-center">
              <div className="w-14 h-14 rounded-2xl bg-plum/10 flex items-center justify-center">
                <Calendar size={24} className="text-plum" />
              </div>
              <div>
                <p className="font-semibold text-small text-midnight">
                  {activeFilterCount > 0 ? 'No matching events' : 'Nothing scheduled'}
                </p>
                <p className="text-[13px] text-slate mt-1">
                  {activeFilterCount > 0
                    ? 'Try adjusting your filters.'
                    : `No moves or estimates on ${selectedDate === today ? 'today' : formatMoveDate(selectedDate)}.`}
                </p>
              </div>
            </div>
          )}

          {/* Moves section */}
          {moveEvents.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-50">
                  <Truck size={11} className="text-blue-600" />
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
                  Moves ({moveEvents.length})
                </p>
              </div>
              <div className="space-y-2">
                {moveEvents.map(ev => (
                  <ScheduleMoveCard key={ev.id} event={ev} onClick={() => handleEventClick(ev)} />
                ))}
              </div>
            </div>
          )}

          {/* Estimates section */}
          {estimateEvents.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded flex items-center justify-center bg-amber-50">
                  <ClipboardList size={11} className="text-amber-600" />
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
                  Estimates ({estimateEvents.length})
                </p>
              </div>
              <div className="space-y-2">
                {estimateEvents.map(ev => (
                  <ScheduleMoveCard key={ev.id} event={ev} onClick={() => handleEventClick(ev)} />
                ))}
              </div>
            </div>
          )}

          {/* Resource summary — always visible */}
          <ResourceSummary resources={resources} dayEvents={dayEvents} allEvents={events} selectedDate={selectedDate} today={today} mode={resourceMode} onModeChange={setResourceMode} />
        </div>
      )}

      {/* Day agenda — desktop layout (side-by-side with resource sidebar) */}
      {!loading && !error && (
        <div className="hidden lg:grid lg:grid-cols-[1fr_280px] lg:gap-5">
          {/* Events column */}
          <div className="space-y-4">
            {/* Empty state for events */}
            {dayEvents.length === 0 && (
              <div className="flex flex-col items-center py-16 gap-3 text-center">
                <div className="w-14 h-14 rounded-2xl bg-plum/10 flex items-center justify-center">
                  <Calendar size={24} className="text-plum" />
                </div>
                <div>
                  <p className="font-semibold text-small text-midnight">
                    {activeFilterCount > 0 ? 'No matching events' : 'Nothing scheduled'}
                  </p>
                  <p className="text-[13px] text-slate mt-1">
                    {activeFilterCount > 0
                      ? 'Try adjusting your filters.'
                      : `No moves or estimates on ${selectedDate === today ? 'today' : formatMoveDate(selectedDate)}.`}
                  </p>
                </div>
              </div>
            )}

            {moveEvents.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-50">
                    <Truck size={11} className="text-blue-600" />
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
                    Moves ({moveEvents.length})
                  </p>
                </div>
                <div className="space-y-2">
                  {moveEvents.map(ev => (
                    <ScheduleMoveCard key={ev.id} event={ev} onClick={() => handleEventClick(ev)} />
                  ))}
                </div>
              </div>
            )}

            {estimateEvents.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded flex items-center justify-center bg-amber-50">
                    <ClipboardList size={11} className="text-amber-600" />
                  </div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
                    Estimates ({estimateEvents.length})
                  </p>
                </div>
                <div className="space-y-2">
                  {estimateEvents.map(ev => (
                    <ScheduleMoveCard key={ev.id} event={ev} onClick={() => handleEventClick(ev)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resource sidebar — always visible */}
          <div className="space-y-4">
            <ResourceSummary resources={resources} dayEvents={dayEvents} allEvents={events} selectedDate={selectedDate} today={today} sidebar mode={resourceMode} onModeChange={setResourceMode} />
          </div>
        </div>
      )}

      {/* Filter sheet */}
      {showFilterSheet && (
        <ScheduleFilterSheet
          filters={filters}
          onApply={setFilters}
          onClose={() => setShowFilterSheet(false)}
        />
      )}
    </>
  );
}

// ── Resource Summary ─────────────────────────────────────────────────────────

function ResourceSummary({
  resources,
  dayEvents,
  allEvents,
  selectedDate,
  today,
  sidebar,
  mode,
  onModeChange,
}: {
  resources: ScheduleResources;
  dayEvents: ScheduleEvent[];
  allEvents: ScheduleEvent[];
  selectedDate: string;
  today: string;
  sidebar?: boolean;
  mode: 'today' | 'weekly';
  onModeChange: (mode: 'today' | 'weekly') => void;
}) {
  // Compute resource breakdown from the relevant event set
  const sourceEvents = mode === 'weekly' ? allEvents : dayEvents;

  const computedResources = useMemo(() => {
    const moverNames = new Set<string>();
    const estimatorNames = new Set<string>();
    const contractorNames = new Set<string>();
    const truckNames = new Set<string>();

    for (const ev of sourceEvents) {
      if (ev.type === 'move') {
        if (ev.assignedCrew) {
          for (const c of ev.assignedCrew) {
            if (c.isContractor) {
              contractorNames.add(c.displayName);
            } else {
              moverNames.add(c.displayName);
            }
          }
        }
        if (ev.assignedTrucks) {
          for (const t of ev.assignedTrucks) {
            truckNames.add(t.truckName);
          }
        }
      } else if (ev.type === 'estimate') {
        if (ev.quoteLeadDisplayName) {
          estimatorNames.add(ev.quoteLeadDisplayName);
        }
      }
    }

    return {
      moverNames: [...moverNames],
      estimatorNames: [...estimatorNames],
      contractorNames: [...contractorNames],
      truckNames: [...truckNames],
    };
  }, [sourceEvents]);

  const categories: {
    label: string;
    icon: React.ReactNode;
    busy: number;
    total: number | null;
    names: string[];
    barColor: string;
  }[] = [
    {
      label: 'Movers',
      icon: <Users size={13} />,
      busy: computedResources.moverNames.length,
      total: resources.totalMovers || null,
      names: computedResources.moverNames,
      barColor: 'bg-plum',
    },
    {
      label: 'Estimators',
      icon: <UserCheck size={13} />,
      busy: computedResources.estimatorNames.length,
      total: resources.totalEstimators || null,
      names: computedResources.estimatorNames,
      barColor: 'bg-amber-500',
    },
    {
      label: 'Contractors',
      icon: <HardHat size={13} />,
      busy: computedResources.contractorNames.length,
      total: null,
      names: computedResources.contractorNames,
      barColor: 'bg-emerald-500',
    },
    {
      label: 'Trucks',
      icon: <Truck size={13} />,
      busy: computedResources.truckNames.length,
      total: resources.totalOrgTrucks || null,
      names: computedResources.truckNames,
      barColor: 'bg-blue-500',
    },
  ];

  return (
    <div className={`bg-mist rounded-xl p-3.5 border border-stone/50 ${sidebar ? '' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate">
          Resources
        </p>
        <div className="flex h-7 rounded-lg border border-stone bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => onModeChange('today')}
            className={`px-2.5 text-[11px] font-semibold transition-colors
              ${mode === 'today' ? 'bg-plum text-white' : 'text-slate hover:text-midnight'}`}
          >
            {selectedDate === today ? 'Today' : 'Day'}
          </button>
          <button
            type="button"
            onClick={() => onModeChange('weekly')}
            className={`px-2.5 text-[11px] font-semibold transition-colors
              ${mode === 'weekly' ? 'bg-plum text-white' : 'text-slate hover:text-midnight'}`}
          >
            Weekly
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {categories.map(cat => (
          <div key={cat.label}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 text-[12px] text-midnight font-medium">
                {cat.icon}
                <span>{cat.label}</span>
              </div>
              <span className="text-[12px] font-mono font-semibold text-midnight">
                {cat.total != null ? `${cat.busy}/${cat.total}` : cat.busy}
              </span>
            </div>
            {cat.total != null && cat.total > 0 && (
              <div className="w-full h-2 bg-stone/60 rounded-full overflow-hidden">
                <div
                  className={`h-full ${cat.barColor} rounded-full transition-all duration-300`}
                  style={{ width: `${Math.min(100, (cat.busy / cat.total) * 100)}%` }}
                />
              </div>
            )}
            {cat.busy > 0 && (
              <p className="text-[11px] text-slate mt-1.5">
                {cat.names.join(', ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
